import { FeatureResponse, No, Unclear, Unresolved, Yes } from './FeatureResponse';
import { Syndrome } from './Syndrome';
import { defaultSeizure, getCompatibleSeizures } from './Seizures';
import { hasFeature } from './Utils';
import { Development } from './Constants';

export class SeLECTS extends Syndrome {
    
    getName() {
        return "SeLECTS";
    }

    checkAgeAndGender(features: Map<string, any>): FeatureResponse {
        let mandatory = super.checkAge(features, 4, 10);
        if(mandatory.isYes()) {
            return new Yes("Mandatory present: Age at onset between 4 and 10y.");
        }
        let younger = super.checkAge(features, 0, 3);
        if(younger.isYes()) {
            return new No("Exclusionary: Age at onset <3y.");
        }
        let older = super.checkAge(features, 14, 999);
        if(older.isYes()) {
            return new No("Exclusionary: Age at onset >14y.");
        }
        
        return new Unclear();
    }

    public checkMandatorySeizures(seizures: any[]): FeatureResponse {
        let s1 = defaultSeizure();
        s1.mainType = "focal";
        s1.onset = "motor";
        s1.details = ["motor_clonic"];
        const hasS1 = this.containsSeizure(seizures, s1);

        const annot: string[] = new Array();
        if(hasS1) {
            annot.push("Mandatory: Focal clonic seizures with focal to bilateral tonic clonic seizures present.");
        } else {
            annot.push("Mandatory focal clonic seizures with focal to bilateral tonic clonic seizures missing.");
        }

        if(hasS1) {   
            return new Yes(annot);
        } else {
            return new No(annot);
        }
    }

    public checkExclusionarySeizures(seizures: any[]): FeatureResponse {
        let s1 = defaultSeizure();
        s1.mainType = "focal";
        s1.onset = "nonmotor";
        s1.details = ["nonmotor_autonomic"];
        const autonomic = this.containsSeizure(seizures, s1);

        s1.details = ["nonmotor_emotional"];
        const emotional = this.containsSeizure(seizures, s1);

        s1.details = ["nonmotor_sensory"];
        const sensory = this.containsSeizure(seizures, s1);

        s1 = defaultSeizure();
        s1.mainType = "generalized";
        s1.onset = "motor";
        s1.details = ["motor_gen_tonic_clonic"];
        const gen_tc = this.containsSeizure(seizures, s1);

        s1.onset = "nonmotor";
        s1.details = ["nonmotor_gen_atypical"];
        const gen_atypical = this.containsSeizure(seizures, s1);

        const annot: string[] = new Array();
        if(autonomic) {
            annot.push("Exclusionary: Focal nonmotor onset autonomic seizures present.");
        }
        if(emotional) {
            annot.push("Exclusionary: Focal nonmotor emotional seizures present.");
        } 
        if(sensory) {
            annot.push("Exclusionary: Focal nonmotor sensory seizures present.");
        } 
        if(gen_tc) {
            annot.push("Exclusionary: Generalized tonic-clonic seizures present.");
        }
        if(gen_atypical) {
            annot.push("Exclusionary: Generalized atypical absence seizures present.");
        } 

        if(autonomic || emotional || sensory || gen_tc || gen_atypical) {
            return new No(annot);
        }
        return new Yes();
    }

    protected checkDevelopmentDetails(dev: Development[], devAtOnsetAvailable: boolean, devAtCourseAvailable: boolean): FeatureResponse {
        if(!devAtOnsetAvailable) {
            return new Unresolved("Info on development at onset is not available however is critical to determine exlusionary neurocognitive regression.");
        }

        if(hasFeature(dev, "dev_regression")) {
            return new No("Exlusionary: Neurocognitive regression");
        }
        return new Unclear();
    }

    protected checkEEGDetails(eeg: string[], eegAvailable: boolean): FeatureResponse {
        if(!eegAvailable) {
            return new Unresolved("EEG is not available however is critical to determine mandatory and exlusionary features.");
        }
        
        const annot: string[] = new Array();
        const centrotemporal = this.checkFeatures(eeg, ["eeg_ied_central", "eeg_ied_temporal"], true, annot);
        
        if(centrotemporal) {
            return new Yes(annot);
        } else {
            return new Unclear();
        }
    }

    protected checkImagingDetails(imaging: string[], imagingAvailable: boolean): FeatureResponse {
        if(!imagingAvailable) {
            return new Unresolved("Imaging is not available however is critical to determine mandatory and exlusionary features.");
        }
        
        const annot: string[] = new Array();
        const exclusionaryOk = this.checkFeatures(imaging, ["imaging_lesion"], false, annot);

        if(exclusionaryOk) {
            return new Yes(annot);
        } else {
            return new Unclear();
        }
    }

    getAlertCriteria(): string[] {
        return ["Age at onset >12y", "Focal motor or generalized convulsive SE >30 minutes", "Typical seizure frequency greater than daily", "Daytime seizures only",
                "Moderate or profound intellectual disability", "Any abnormal neurological sign or sensory deficit",
                "Sustained focal slowing not limited to the post-ictal phase", "Persistently unilateral centrotemporal abnormalities on serial EEGs", "Lack of sleep activation of centrotemporal abnormalities"];
    }

}